import { useState, useCallback, useEffect } from "react";
import { orgMemoryManagerService } from "../services/orgMemoryManagerService";
import type { MemoryStructure, MemorySection, MemoryEntry, QualityFeedback } from "../types/orgMemory";

export type UseOrgMemoryReturn = {
  structure: MemoryStructure | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  updateEntry: (sectionId: string, entryId: string, updates: Partial<Pick<MemoryEntry, "question" | "answer">>) => void;
  saveEntry: (sectionId: string, entryId: string, updates: Partial<Pick<MemoryEntry, "answer">>) => Promise<void>;
  deleteEntry: (sectionId: string, entryId: string) => void;
  updateSection: (sectionId: string, updates: Partial<Pick<MemorySection, "category" | "tags" | "documentTitle">>) => void;
  deleteSection: (sectionId: string) => void;
  updateGlobal: (updates: Partial<Pick<MemoryStructure, "categories" | "globalTags">>) => void;
  updateFeedback: (feedback: QualityFeedback[]) => void;
  save: () => Promise<void>;
  reload: () => void;
};

export const useOrgMemory = (): UseOrgMemoryReturn => {
  const [structure, setStructure] = useState<MemoryStructure | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setStructure(await orgMemoryManagerService.getStructure());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load memory");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateEntry = useCallback(
    (sectionId: string, entryId: string, updates: Partial<Pick<MemoryEntry, "question" | "answer">>) =>
      setStructure((prev) =>
        prev
          ? {
              ...prev,
              sections: prev.sections.map((s) =>
                s.id !== sectionId
                  ? s
                  : { ...s, entries: s.entries.map((e) => (e.id !== entryId ? e : { ...e, ...updates })) }
              ),
            }
          : prev
      ),
    []
  );

  const saveEntry = useCallback(
    async (sectionId: string, entryId: string, updates: Partial<Pick<MemoryEntry, "answer">>) => {
      let next: MemoryStructure | null = null;
      setStructure((prev) => {
        if (!prev) return prev;
        next = {
          ...prev,
          sections: prev.sections.map((s) =>
            s.id !== sectionId
              ? s
              : { ...s, entries: s.entries.map((e) => (e.id !== entryId ? e : { ...e, ...updates })) }
          ),
        };
        return next;
      });
      if (!next) return;
      setIsSaving(true);
      setError(null);
      try {
        await orgMemoryManagerService.saveStructure(next);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to save";
        setError(msg);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  const deleteEntry = useCallback(
    (sectionId: string, entryId: string) =>
      setStructure((prev) =>
        prev
          ? {
              ...prev,
              sections: prev.sections
                .map((s) =>
                  s.id !== sectionId ? s : { ...s, entries: s.entries.filter((e) => e.id !== entryId) }
                )
                .filter((s) => s.entries.length > 0),
            }
          : prev
      ),
    []
  );

  const updateSection = useCallback(
    (sectionId: string, updates: Partial<Pick<MemorySection, "category" | "tags" | "documentTitle">>) =>
      setStructure((prev) =>
        prev
          ? { ...prev, sections: prev.sections.map((s) => (s.id !== sectionId ? s : { ...s, ...updates })) }
          : prev
      ),
    []
  );

  const deleteSection = useCallback(
    (sectionId: string) =>
      setStructure((prev) =>
        prev ? { ...prev, sections: prev.sections.filter((s) => s.id !== sectionId) } : prev
      ),
    []
  );

  const updateGlobal = useCallback(
    (updates: Partial<Pick<MemoryStructure, "categories" | "globalTags">>) =>
      setStructure((prev) => (prev ? { ...prev, ...updates } : prev)),
    []
  );

  const updateFeedback = useCallback(
    (feedback: QualityFeedback[]) =>
      setStructure((prev) => (prev ? { ...prev, feedback } : prev)),
    []
  );

  const save = useCallback(async () => {
    if (!structure) return;
    setIsSaving(true);
    setError(null);
    try {
      await orgMemoryManagerService.saveStructure(structure);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save";
      setError(msg);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [structure]);

  return { structure, isLoading, isSaving, error, updateEntry, saveEntry, deleteEntry, updateSection, deleteSection, updateGlobal, updateFeedback, save, reload: load };
};
